import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import TabSwitcher from '@/components/base/TabSwitcher';
import SizeTable from '@/components/base/SizeTable';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, categories } from '@/mocks/articles';
import { shoeSizeData, clothingSizeData, kidsSizeData } from '@/mocks/categories';

const categoryMap: Record<string, { name: string; icon: string; description: string }> = {};
categories.forEach((c) => {
  categoryMap[c.slug] = { name: c.name, icon: c.icon, description: c.description };
});

export default function CategoryPage() {
  const { type = 'shoes' } = useParams<{ type: string }>();
  const category = categoryMap[type] || categoryMap.shoes;
  const [activeTab, setActiveTab] = useState('women');

  useEffect(() => {
    document.title = `${category.name}亚洲五码对照表——精准${category.name}换算`;
    setActiveTab('women');
  }, [type, category.name]);

  const filteredArticles = articles.filter((a) => a.categorySlug === type);

  const getShoeColumns = () => [
    { key: 'footLength', label: '脚长(mm)' },
    { key: 'cn', label: '中国码', highlight: true },
    { key: 'jp', label: '日本码' },
    { key: 'eu', label: '欧码' },
    { key: 'us', label: '美码' },
    { key: 'uk', label: '英码' },
  ];

  const getClothingColumns = () => {
    if (activeTab === 'pants') {
      return [
        { key: 'asian', label: '亚洲码', highlight: true },
        { key: 'eu', label: '欧码' },
        { key: 'us', label: '美码' },
        { key: 'waist', label: '腰围(cm)' },
        { key: 'hip', label: '臀围(cm)' },
      ];
    }
    if (activeTab === 'men') {
      return [
        { key: 'asian', label: '亚洲码', highlight: true },
        { key: 'eu', label: '欧码' },
        { key: 'us', label: '美码' },
        { key: 'bust', label: '胸围(cm)' },
        { key: 'shoulder', label: '肩宽(cm)' },
      ];
    }
    return [
      { key: 'asian', label: '亚洲码', highlight: true },
      { key: 'eu', label: '欧码' },
      { key: 'us', label: '美码' },
      { key: 'bust', label: '胸围(cm)' },
      { key: 'waist', label: '腰围(cm)' },
    ];
  };

  const getKidsShoeColumns = () => [
    { key: 'footLength', label: '脚长(mm)' },
    { key: 'cn', label: '中国码', highlight: true },
    { key: 'eu', label: '欧码' },
    { key: 'us', label: '美码' },
    { key: 'uk', label: '英码' },
    { key: 'age', label: '建议年龄' },
  ];

  const getKidsClothingColumns = () => [
    { key: 'height', label: '身高(cm)' },
    { key: 'asian', label: '亚洲码', highlight: true },
    { key: 'eu', label: '欧码' },
    { key: 'us', label: '美码' },
    { key: 'age', label: '建议年龄' },
  ];

  const renderShoeContent = () => (
    <>
      <div className="flex justify-center mb-8">
        <TabSwitcher
          tabs={[
            { key: 'women', label: '女鞋' },
            { key: 'men', label: '男鞋' },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>
      <SizeTable
        title={activeTab === 'women' ? '女鞋尺码对照表' : '男鞋尺码对照表'}
        columns={getShoeColumns()}
        data={activeTab === 'women' ? shoeSizeData.women : shoeSizeData.men}
      />
    </>
  );

  const renderClothingContent = () => (
    <>
      <div className="flex justify-center mb-8">
        <TabSwitcher
          tabs={[
            { key: 'women', label: '女装上衣' },
            { key: 'men', label: '男装上衣' },
            { key: 'pants', label: '裤装' },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>
      <SizeTable
        title={
          activeTab === 'women'
            ? '女装上衣尺码对照表'
            : activeTab === 'men'
              ? '男装上衣尺码对照表'
              : '裤装尺码对照表'
        }
        columns={getClothingColumns()}
        data={
          activeTab === 'women'
            ? clothingSizeData.womenTop
            : activeTab === 'men'
              ? clothingSizeData.menTop
              : clothingSizeData.pants
        }
      />
    </>
  );

  const renderKidsContent = () => (
    <>
      <div className="flex justify-center mb-8">
        <TabSwitcher
          tabs={[
            { key: 'shoes', label: '童鞋' },
            { key: 'clothing', label: '童装' },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>
      <SizeTable
        title={activeTab === 'shoes' ? '童鞋尺码对照表' : '童装尺码对照表'}
        columns={activeTab === 'shoes' ? getKidsShoeColumns() : getKidsClothingColumns()}
        data={activeTab === 'shoes' ? kidsSizeData.shoes : kidsSizeData.clothing}
      />
    </>
  );

  const renderContent = () => {
    if (type === 'clothing') return renderClothingContent();
    if (type === 'kids') return renderKidsContent();
    return renderShoeContent();
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${category.name}亚洲五码对照表`,
    description: `${category.name}亚洲五码与欧码、美码、英码完整对照表，帮助消费者精准选码。`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://docbihd.cn' },
        { '@type': 'ListItem', position: 2, name: category.name },
      ],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ name: '首页', path: '/' }, { name: category.name }]} />

        <section className="w-full px-4 md:px-6 py-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="w-14 h-14 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mx-auto mb-4">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {category.name}亚洲五码对照表
              </h1>
              <p className="text-foreground-600 text-sm max-w-lg mx-auto">{category.description}</p>
            </div>

            {renderContent()}

            {type === 'shoes' && (
              <div className="mt-10 p-5 bg-secondary-50 rounded-lg border border-secondary-200/60">
                <h3 className="text-sm font-semibold text-foreground-900 mb-3">鞋码换算公式</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-foreground-700">
                  <div>
                    <span className="font-medium text-foreground-900">亚洲五码→欧码：</span>
                    欧码 ≈ (中国码 ÷ 5) × 2 + 1
                  </div>
                  <div>
                    <span className="font-medium text-foreground-900">亚洲五码→美码（男）：</span>
                    美码 ≈ (中国码 - 220) ÷ 5 × 3 + 5
                  </div>
                  <div>
                    <span className="font-medium text-foreground-900">亚洲五码→英码（男）：</span>
                    英码 ≈ (中国码 - 220) ÷ 5 × 3 + 4
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {filteredArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 bg-background-100/70">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl font-bold text-foreground-950 mb-8">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.id} {...article} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="w-full px-4 md:px-6 py-12 bg-background-50">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-lg font-semibold text-foreground-950 mb-3">浏览其他品类</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {categories.filter((c) => c.slug !== type).map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="px-5 py-2.5 border border-background-300/60 rounded-full text-sm text-foreground-700 whitespace-nowrap cursor-pointer hover:border-primary-400 hover:text-primary-600 transition-colors"
                >
                  {c.name}对照
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}